import { Box, Typography, Paper } from '@mui/material';
import { SmartContentDisplay } from './SmartContentDisplay';

export type SmartContentDisplayStoryProps = {
  maxHeight: number;
  minDelta: number;
  expandText: string;
  collapseText: string;
  contentType: 'short' | 'medium' | 'long' | 'very-long';
};

export const SmartContentDisplayStory = ({
  maxHeight,
  minDelta,
  expandText,
  collapseText,
  contentType,
}: SmartContentDisplayStoryProps) => {
  const getContent = () => {
    switch (contentType) {
      case 'short':
        return (
          <Typography>
            This is a short piece of content that will be displayed completely
            without any gradient effect. It&apos;s under the height threshold,
            so it shows normally.
          </Typography>
        );
      case 'medium':
        return (
          <>
            <Typography paragraph>
              This is a medium-length piece of content that demonstrates the
              SmartContentDisplay component. It will be truncated at the
              specified height and show a gradient overlay with an expand
              button.
            </Typography>
            <Typography>
              The gradient height is automatically calculated as 40% of the
              maxHeight with reasonable bounds.
            </Typography>
          </>
        );
      case 'long':
        return (
          <>
            <Typography paragraph>
              This is a longer piece of content that will definitely trigger the
              gradient effect. When the content exceeds the specified height, it
              will be truncated and show a gradient at the bottom.
            </Typography>
            <Typography paragraph>
              The gradient creates a nice fade effect that indicates
              there&apos;s more content below. Users can click the expand button
              to see the full content.
            </Typography>
            <Typography>
              This approach provides a much better user experience than simple
              truncation.
            </Typography>
          </>
        );
      case 'very-long':
        return (
          <>
            <Typography paragraph>
              This is the first paragraph of a very long piece of content. It
              contains multiple paragraphs to demonstrate how the
              SmartContentDisplay handles complex content structures with
              various lengths.
            </Typography>
            <Typography paragraph>
              The second paragraph continues with more content that will push
              the total height well beyond the specified threshold. This ensures
              the gradient effect and expand button are properly displayed.
            </Typography>
            <Typography paragraph>
              The third paragraph adds even more content to make sure we have
              enough text to trigger the smart display behavior. This
              demonstrates the component&apos;s ability to handle various
              content lengths.
            </Typography>
            <Typography paragraph>
              Finally, this fourth paragraph ensures we have sufficient content
              to showcase the full functionality of the SmartContentDisplay
              component, including the gradient effect and expand/collapse
              behavior.
            </Typography>
            <Typography paragraph>
              The component automatically calculates the optimal gradient height
              and provides smooth animations for expanding and collapsing
              content, making it perfect for displaying long-form content in
              limited space.
            </Typography>
          </>
        );
      default:
        return <Typography>Invalid content type</Typography>;
    }
  };

  return (
    <Box sx={{ p: 2, maxWidth: 600 }}>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          {contentType.charAt(0).toUpperCase() +
            contentType.slice(1).replace('-', ' ')}{' '}
          Content
          {maxHeight && ` (Max Height: ${maxHeight}px)`}
        </Typography>
        <SmartContentDisplay
          maxHeight={maxHeight}
          minDelta={minDelta}
          expandText={expandText}
          collapseText={collapseText}
        >
          {getContent()}
        </SmartContentDisplay>
      </Paper>
    </Box>
  );
};
